export class ObservableRegistry<TEntry, TSnapshot> {
  private items = new Map<string, TEntry>();
  private listeners = new Set<() => void>();
  protected snapshot: TSnapshot[] = [];

  constructor(private readonly project: (entry: TEntry) => TSnapshot) {}

  protected upsert(key: string, entry: TEntry): () => void {
    this.items.set(key, entry);
    this.notify();
    return () => {
      this.items.delete(key);
      this.notify();
    };
  }

  protected upsertMany(entries: readonly [string, TEntry][]): () => void {
    for (const [key, entry] of entries) this.items.set(key, entry);
    this.notify();
    return () => {
      for (const [key] of entries) this.items.delete(key);
      this.notify();
    };
  }

  protected remove(key: string): void {
    if (this.items.delete(key)) this.notify();
  }

  get(key: string): TEntry | undefined {
    return this.items.get(key);
  }

  // By key, not by registration order: entries register as components mount,
  // in an order that varies run to run, and the snapshot is part of what
  // Genie is sent (and of its cache key).
  protected values(): TEntry[] {
    return Array.from(this.items.entries())
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([, entry]) => entry);
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): TSnapshot[] => this.snapshot;

  private notify(): void {
    this.snapshot = this.values().map(this.project);
    for (const listener of this.listeners) listener();
  }
}
